import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';

import i18n from 'i18n-js';
import { buildUrl } from 'utils';
import { useConfirm } from 'hooks';
import Lightbox from 'react-image-lightbox';
import { Card, CollapseCard } from 'components/UI';
import Loading from 'components/Loading';
import { usersActions } from 'state/modules/users';
import { UploadDocumentModal } from 'components/Modals';
import { FETCH_STATE } from 'constants/common';
import { DocumentsActions } from 'state/modules/documents';
import { ReservationsActions } from 'state/modules/reservations';
import { SubscriptionsActions } from 'state/modules/subscriptions';
import { qualificationsActions } from 'state/modules/qualifications';
import { Box, Typography, LinearProgress } from '@material-ui/core';
import QualificationRequirements from './QualificationRequirements';
import { useStyles, collapseCardStyles } from './Qualifications.styled';

const Qualifications = ({ id, type, title, history }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [data, setUploadData] = useState({});
  const collapseCardClasses = collapseCardStyles();
  const [bypassData, setBypassData] = useState(null);
  const [qualifications, setQualifications] = useState({});
  const { qualsDocuments, fetchedUploadDocument } = useSelector((state) => state.documents || {});
  const [uploadQualification, setUploadQualification] = useState(null);
  const [openUploadDocumentModal, setOpenUploadDocumentModal] = useState(false);
  const { fetchedExchange } = useSelector((state) => state.subscriptions || {});
  const stateQualifications = useSelector((state) => state?.qualifications?.qualifications) || {};
  const { fetchingQualifications } = useSelector((state) => state?.qualifications);
  const config = useSelector((state) => state.config?.settings);
  const [displayRequirementData, setDisplayRequirementData] = useState([]);

  useEffect(() => {
    const requirementData = config?.integrations?.settings?.display_requirement_data;
    if (requirementData) setDisplayRequirementData(requirementData);
  }, [config]);
  /**
   * Update user qualifications
   * @param {object} payload
   */
  const handleBypass = useCallback(
    (qualificationId, requirementSlug, payload) => {
      dispatch(
        qualificationsActions.fetchUpdateQualification(
          qualificationId,
          type,
          requirementSlug,
          payload
        )
      );
    },
    [dispatch, type]
  );

  /**
   * Handle confirm bypass modal
   */
  const [toggleBypassConfirm, BypassConfirm] = useConfirm({
    title: i18n.t('common.are_you_sure'),
    message: bypassData?.message,
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.confirm'),
    onConfirm: useCallback(
      () => handleBypass(bypassData.qualification, bypassData.requirement, bypassData.payload),
      [handleBypass, bypassData]
    ),
  });

  const [lightboxProperties, setLightboxProperties] = useState({
    isOpen: false,
    photoIndex: 0,
  });

  /**
   * Get document for current user
   */
  const getDocuments = () => {
    const payload = {
      page: 1,
      limit: 20,
      source_id: id,
      source_type: type,
    };
    dispatch(DocumentsActions.fetchDocumentsById(payload));
  };

  /**
   * Change document status
   */
  const handleUpdateDocument = async (documentId, qualificationId, payload) => {
    await dispatch(DocumentsActions.fetchChangeDocumentStatus(documentId, payload));
    if (type === 'User') {
      dispatch(usersActions.fetchUserById(id));
    } else if (type === 'Reservation') {
      dispatch(ReservationsActions.fetchReservationById(id));
    } else {
      dispatch(SubscriptionsActions.fetchSubscriptionById(id));
    }
    dispatch(qualificationsActions.fetchQualificationById(qualificationId, type));
  };

  /**
   * Navigate to invoice
   */
  const handleInvoiceClick = (id) => {
    history.push(buildUrl(`/invoices/${id}`));
  };

  /**
   * Handle open upload document modal
   * @param {object} event Files input event
   */
  const handleOpenUploadModal = (requirement, qualificationId = '', exit = false) => {
    let _data = {};
    if (!exit) {
      _data = {
        sourceId: id,
        sourceType: type,
        key: requirement.slug,
      };
    }
    setUploadData(_data);
    setOpenUploadDocumentModal((prevValue) => !prevValue);
    if (qualificationId) {
      setUploadQualification(qualificationId);
    }
  };

  /**
   * Handle manual approval
   * @param {object} requirement
   * @param {object} qualification
   * @param {boolean} approved
   */
  const handleManualApprove = (requirement, qualification, approved) => {
    const payload = {
      source_type: type,
      source_id: id,
      data: {
        approved: approved,
      },
    };

    setBypassData({
      message: `${i18n.t('qualifications.manula_approve_msg_1')} ${i18n.t(
        approved ? 'approve' : 'decline'
      )} ${i18n.t('qualifications.manula_approve_msg_2')} `,
      requirement: requirement.slug,
      qualification: qualification.id,
      payload,
    });
    toggleBypassConfirm();
  };

  /**
   * Handle clear
   * @param {object} requirement
   * @param {object} qualification
   * @param {boolean} approved
   */
  const handleClear = (requirement, qualification) => {
    const payload = {
      source_type: type,
      source_id: id,
      data: { clear: true },
    };

    setBypassData({
      message: i18n.t('qualifications.clear_msg_1'),
      requirement: requirement.slug,
      qualification: qualification.id,
      payload,
    });
    toggleBypassConfirm();
  };

  /**
   * Handle view document
   */
  const handleViewDocument = (docId) => {
    const path = qualsDocuments?.[id]?.find((el) => el.id === docId);
    if (!path) return;
    const type = path?.url_path?.includes('pdf') ? 'pdf' : 'image';

    if (type === 'pdf') {
      window.open(path.url_path);
    } else {
      setLightboxProperties({
        ...lightboxProperties,
        isOpen: true,
        images: [{ ...path }],
      });
    }
  };

  /**
   * Handle bypass toggle
   * @param {*} e Bypassed value
   * @param {*} requirement Slug of the requirement
   * @param {*} qualification Slug of the qualification
   */
  const handleBypassToggle = (e, requirement, qualification) => {
    const payload = {
      bypass: e.target.checked,
      source_type: type,
      source_id: id,
    };

    if (payload.bypass) {
      setBypassData({
        message: `${i18n.t('qualifications.bypass_msg_1')} "${requirement.label}". 
                  ${i18n.t('qualifications.bypass_msg_2')} "${qualification.label}"
                  ${i18n.t('qualifications.bypass_msg_3')}`,
        requirement: requirement.slug,
        qualification: qualification.id,
        payload,
      });
      toggleBypassConfirm();
    } else {
      handleBypass(qualification.id, requirement.slug, payload);
    }
  };

  /**
   * Verify if has failed requirements on the qualifications
   * @param {object} qualification Object of the qualification
   */
  const hasFailedRequirement = (qualification) =>
    Object.values(qualification?.requirements || {}).filter((val) => {
      return val.status === 'FAILED';
    });

  /**
   * Verify if has requirements in progress/passed/bypassed on the qualifications
   * @param {object} qualification Object of the qualification
   */
  const hasInProgressRequirement = (qualification) =>
    Object.values(qualification?.requirements || {}).filter(
      (val) =>
        val.status === 'INPROGRESS' || (val.status === 'PASSED') | (val.status === 'BYPASSED')
    );

  /**
   * Get status of the qualification
   * @param {object} qualification Object of the qualification
   */
  const getQualificationStatus = (qualification) => {
    let status = qualification?.status || 'NOTSTARTED';
    if (status === 'INPROGRESS') {
      status = 'IN PROGRESS';
    } else if (status === 'NOTSTARTED') {
      status = 'NOT STARTED';
    }
    return status.toLocaleLowerCase();
  };

  const fetchQuals = () => {
    const params = {
      obj_type: type,
      obj_id: id,
    };
    dispatch(qualificationsActions.fetchQualifications(type, params));
  };

  /**
   * Render progress bar of the qualification accorfing to status
   * @param {object} qualification Object of the qualification
   */
  const renderProgressBar = (qualification, showQuantity = true) => {
    const status = getQualificationStatus(qualification);
    const requirements = Object.keys(qualification?.requirements || {}).length;
    let quantity = '';
    let style = '';
    let value = 0;
    let size = 0;

    switch (status) {
      case 'completed':
        quantity = `${requirements}/${requirements}`;
        style = 'barCompleted';
        value = 100;
        break;

      case 'failed':
        size = hasFailedRequirement(qualification);
        quantity = `${size.length}/${requirements}`;
        style = 'barFailed';
        value = (size.length * 100) / requirements;
        break;

      case 'in progress':
        size = hasInProgressRequirement(qualification);
        quantity = `${size.length}/${requirements}`;
        style = 'barInProgress';
        value = (size.length * 100) / requirements;
        break;

      default:
        quantity = `0/${requirements}`;
        style = 'barNotStarted';
        value = 0;
        break;
    }

    return (
      <Box display="flex" alignItems="center">
        <Box style={{ width: 86 }} mr={1}>
          <LinearProgress value={value} variant="determinate" className={classes[style]} />
        </Box>
        {showQuantity && (
          <Typography>
            <Box fontSize={12} component="span">
              {quantity}
            </Box>
          </Typography>
        )}
      </Box>
    );
  };

  useEffect(() => {
    setQualifications(stateQualifications[type] || null);
  }, [stateQualifications]); // eslint-disable-line

  useEffect(() => {
    if (id && type) {
      fetchQuals();
      getDocuments();
    }
  }, [id, type]); // eslint-disable-line

  useEffect(() => {
    if (fetchedUploadDocument) {
      if (type === 'User') {
        dispatch(usersActions.fetchUserById(id));
      } else if (type === 'Reservation') {
        dispatch(ReservationsActions.fetchReservationById(id));
      } else {
        dispatch(SubscriptionsActions.fetchSubscriptionById(id));
      }
      if (uploadQualification) {
        dispatch(qualificationsActions.fetchQualificationById(uploadQualification, type));
        setUploadQualification(false);
      }
      getDocuments();
    }
  }, [fetchedUploadDocument]); // eslint-disable-line

  useEffect(() => {
    if (fetchedExchange && type === 'Subscription') {
      fetchQuals();
    }
  }, [fetchedExchange]); // eslint-disable-line

  const fetchRequirements = (id, existsRequermients = false, opened) => {
    if (!existsRequermients && opened) {
      dispatch(qualificationsActions.fetchQualificationById(id, type));
    }
  };

  return (
    <Card
      title={title || i18n.t('qualifications.title')}
      headerActionType="vertical"
      data-cy="qualifications-card"
    >
      <Box className={classes.cardContainer}>
        {qualifications && Object.keys(qualifications).length > 0 ? (
          Object.entries(qualifications).map(([key, value]) => {
            return (
              <Box mt={2} mb={2} key={key}>
                <CollapseCard
                  title={
                    <Box className={classes.cardTitleContainer}>
                      <Box className={classes.cardTitle}>{value.label}</Box>
                      <Box display="flex" justifyContent={'space-between'}>
                        <Box className={classes.cardStatus}>{getQualificationStatus(value)}</Box>
                        <Box display="flex" justifyContent="center" alignItems="center">
                          {renderProgressBar(value, false)}
                        </Box>
                      </Box>
                    </Box>
                  }
                  dataCy={`qualification-${value.label}`}
                  customClasses={collapseCardClasses}
                  opened={false}
                  onCollapse={(opened) =>
                    fetchRequirements(value.id, !!value?.requirements?.length, opened)
                  }
                >
                  <QualificationRequirements
                    loading={fetchingQualifications === FETCH_STATE.LOADING}
                    referenceId={id}
                    qualification={value}
                    requirements={value.requirements}
                    handleBypass={handleBypassToggle}
                    documents={qualsDocuments?.[id] || []}
                    handleViewDocument={handleViewDocument}
                    handleInvoiceClick={handleInvoiceClick}
                    handleManualApprove={handleManualApprove}
                    handleUpdateDocument={handleUpdateDocument}
                    handleUploadDocument={handleOpenUploadModal}
                    handleClear={handleClear}
                    displayRequirementData={displayRequirementData}
                  />
                </CollapseCard>
              </Box>
            );
          })
        ) : fetchingQualifications === FETCH_STATE.LOADING ? (
          <Loading />
        ) : (
          <Typography
            style={{
              fontSize: 14,
              margin: 15,
            }}
          >
            {i18n.t('qualifications.qualification_not_found')}
          </Typography>
        )}
        {lightboxProperties.isOpen && (
          <Box>
            <Lightbox
              mainSrc={lightboxProperties.images[lightboxProperties.photoIndex]?.url_path}
              nextSrc={
                lightboxProperties.images[
                  (lightboxProperties.photoIndex + 1) % qualsDocuments?.[id].length
                ]?.url_path
              }
              prevSrc={
                lightboxProperties.images[
                  (lightboxProperties.photoIndex + lightboxProperties.images.length - 1) %
                    lightboxProperties.images.length
                ]?.url_path
              }
              onCloseRequest={() =>
                setLightboxProperties({
                  ...lightboxProperties,
                  isOpen: false,
                })
              }
              onMovePrevRequest={() =>
                setLightboxProperties({
                  ...lightboxProperties,
                  photoIndex:
                    (lightboxProperties.photoIndex + lightboxProperties.images.length - 1) %
                    lightboxProperties.images.length,
                })
              }
              onMoveNextRequest={() =>
                setLightboxProperties({
                  ...lightboxProperties,
                  isOpen: true,
                  photoIndex:
                    (lightboxProperties.photoIndex + 1) % lightboxProperties.images.length,
                })
              }
              reactModalStyle={{
                overlay: { zIndex: '1300' },
              }}
            />
          </Box>
        )}
        {openUploadDocumentModal && (
          <UploadDocumentModal open={true} data={data} handleClick={handleOpenUploadModal} />
        )}
        <BypassConfirm />
      </Box>
    </Card>
  );
};

Qualifications.propTypes = {
  id: PropTypes.any.isRequired,
  title: PropTypes.string,
  type: PropTypes.oneOf(['User', 'Subscription', 'Reservation']).isRequired,
  history: PropTypes.object,
};

export default withRouter(Qualifications);
